// Browser drags entering an iframe never bubble to the embedding app.
// Keep native events in the preview, highlight the hovered slot, and forward
// dropped bytes with stable page/slot ids to the app's upload pipeline.
export function installExternalPhotoDrop(send, getPageId) {
  if (window.parent === window) return () => {};
  const style = document.createElement("style");
  // Paint above the positioned photo/placeholder children. An inset outline
  // alone can be covered by those children in the actual book templates.
  style.textContent = `
    [data-editable-type="image"][data-external-photo-drop],
    [data-editable-type="image"][data-external-photo-drop]:hover {
      position: relative;
      outline: 6px dashed #405577;
      outline-offset: -6px;
    }
    [data-editable-type="image"][data-external-photo-drop]::after {
      content: "";
      position: absolute;
      top: 0; right: 0; bottom: 0; left: 0;
      box-sizing: border-box;
      border: 6px dashed #405577;
      background-color: rgba(64, 85, 119, 0.18);
      z-index: 20001;
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
  let highlighted = null;
  const clear = () => {
    if (highlighted) highlighted.removeAttribute("data-external-photo-drop");
    highlighted = null;
  };
  const isExternal = (event) => {
    const types = Array.from(event.dataTransfer?.types || []);
    return !types.includes("application/x-babynotebook-photo") &&
      !types.includes("application/x-babynotebook-page") &&
      ["Files", "text/html", "text/uri-list"].some((type) => types.includes(type));
  };
  const enter = (event) => {
    if (!isExternal(event)) return;
    send(JSON.stringify({ type: "external-photo-drag-enter" }));
    over(event);
  };
  const over = (event) => {
    if (!isExternal(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    const slot = event.target.closest?.('[data-editable-type="image"]');
    if (slot !== highlighted) {
      clear();
      highlighted = slot;
      if (highlighted) highlighted.setAttribute("data-external-photo-drop", "true");
    }
  };
  const leave = (event) => {
    if (!highlighted?.contains(event.relatedTarget)) clear();
  };
  const drop = (event) => {
    if (!isExternal(event)) return;
    event.preventDefault();
    clear();
    const slot = event.target.closest?.('[data-editable-type="image"]');
    const questionId = slot?.getAttribute("data-editable-id");
    const pageId = getPageId();
    if (!questionId || !pageId) return;
    // Snapshot all drag data before the first asynchronous FileReader call.
    const transfer = event.dataTransfer;
    const data = {
      html: transfer.getData("text/html"),
      uriList: transfer.getData("text/uri-list"),
      text: transfer.getData("text/plain"),
    };
    const files = Array.from(transfer.files || []);
    Promise.all(files.map((file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ uri: reader.result, name: file.name, type: file.type });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }))).then((files) => {
      send(JSON.stringify({ type: "external-photo-drop", pageId, questionId, data: { ...data, files } }));
    }).catch(() => send(JSON.stringify({ type: "external-photo-drop-error" })));
  };
  document.addEventListener("dragenter", enter);
  document.addEventListener("dragover", over);
  document.addEventListener("dragleave", leave);
  document.addEventListener("drop", drop);
  return () => {
    clear();
    style.remove();
    document.removeEventListener("dragenter", enter);
    document.removeEventListener("dragover", over);
    document.removeEventListener("dragleave", leave);
    document.removeEventListener("drop", drop);
  };
}
